import { SelfContainedCommand, CommandExecutionContext } from '../../../types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../../types/standardToolResults';
import useEntityStore from '../../../stores/entityStore';

export const disableAPIConnectionCommand: SelfContainedCommand = {
  id: 'disable_api_connection',
  description: `Disable an API connection without deleting it.

Sets the connection status to 'inactive' so it won't be used for API calls, but preserves the configuration for potential reactivation later.

**Usage:**
Provide the connectionId of the APIConnection to disable.

**Example:**
\`\`\`json
{
  "connectionId": "apiconn_xyz123"
}
\`\`\``,

  autonomyLevel: 'auto',
  visualTreatment: 'demonstrative',
  
  schema: {
    parameters: [
      {
        name: 'connectionId',
        type: 'string',
        description: 'APIConnection ID to disable',
        required: true,
      },
    ],
  },
  
  examples: {
    good: [
      {
        input: {
          connectionId: 'apiconn_xyz123',
        },
        expectedOutput: {
          status: 'success',
          message: 'API connection disabled',
          format: 'json',
          data: {
            connectionId: 'apiconn_xyz123',
            status: 'inactive',
          },
        },
        description: 'Disable an API connection',
      },
    ],
    bad: [
      {
        input: {
          connectionId: 'invalid_id',
        },
        error: 'APIConnection not found',
        why: 'Connection does not exist',
        fix: 'Use a valid connectionId from the user\'s connections',
      },
    ],
  },
  
  handler: async (params, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const { connectionId } = params;
    
    if (!connectionId) {
      return createErrorResult(
        'connectionId is required',
        'Provide the ID of the APIConnection to disable',
        'validation'
      );
    }
    
    try {
      const entityStore = useEntityStore.getState();
      const connection = entityStore.entities.find(e => e._id === connectionId);
      
      if (!connection || connection.entityType !== 'APIConnection') {
        return createErrorResult(
          'APIConnection not found',
          `No APIConnection found with ID: ${connectionId}`,
          'validation'
        );
      }
      
      if (connection.skeleton?.status === 'inactive') {
        return createSuccessResult(
          `${connection.name} is already disabled`,
          {
            connectionId,
            status: 'inactive',
          },
          {
            metadata: {
              affectedEntities: [connectionId],
            },
          }
        );
      }
      
      // Update status to inactive
      await entityStore.actions.updateEntity(connectionId, {
        skeleton: {
          ...connection.skeleton,
          status: 'inactive',
        },
      });
      
      return createSuccessResult(
        `${connection.name} disabled`,
        {
          connectionId,
          serviceName: connection.skeleton?.serviceName,
          status: 'inactive',
        },
        {
          metadata: {
            affectedEntities: [connectionId],
          },
        }
      );
      
    } catch (error: any) {
      console.error('[disable_api_connection] Error:', error);
      return createErrorResult(
        `Failed to disable API connection: ${error.message}`,
        error,
        'execution'
      );
    }
  },
};
